import React, { useState, useCallback } from 'react'
import {
  View, Text, Pressable, StyleSheet, Modal, TextInput,
  ActivityIndicator, Platform, ScrollView, KeyboardAvoidingView,
  Image, ImageSourcePropType,
} from 'react-native'
import * as WebBrowser from 'expo-web-browser'
import * as Linking from 'expo-linking'
import { X, ChevronLeft, Lock, ExternalLink, Mail, Eye, EyeOff, CheckCircle2, Inbox, Send, Bell } from 'lucide-react-native'
import Animated, { FadeIn, FadeInRight } from 'react-native-reanimated'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'
import { toast } from './Toast'
import { apiClient } from '../api/client'

// ─── Provider Data ──────────────────────────────────────────

type ProviderType = 'oauth' | 'imap'

interface EmailProvider {
  key: string
  name: string
  type: ProviderType
  initials: string
  bgColor: string
  textColor: string
  domains: string[]
  logo?: ImageSourcePropType
  requiresAppPassword?: boolean
  appPasswordUrl?: string
}

const EMAIL_PROVIDERS: EmailProvider[] = [
  // OAuth
  { key: 'google', name: 'Gmail', type: 'oauth', initials: 'G',
    bgColor: '#fce8e6', textColor: '#d93025',
    logo: require('../../assets/provider-logos/gmail.png'),
    domains: ['gmail.com', 'googlemail.com'] },
  { key: 'microsoft', name: 'Outlook', type: 'oauth', initials: 'O',
    bgColor: '#e8f0fe', textColor: '#0078d4',
    logo: require('../../assets/provider-logos/outlook.png'),
    domains: ['outlook.com', 'outlook.dk', 'hotmail.com', 'hotmail.dk', 'live.com', 'live.dk', 'msn.com'] },
  // Danish ISPs
  { key: 'yousee', name: 'YouSee', type: 'imap', initials: 'YS',
    bgColor: '#e6f7e6', textColor: '#009933',
    logo: require('../../assets/provider-logos/yousee-dk.png'),
    domains: ['yousee.dk', 'tdcadsl.dk', 'mail.dk', 'post.tele.dk'] },
  { key: 'telia', name: 'Telia', type: 'imap', initials: 'T',
    bgColor: '#f0e6ff', textColor: '#6600cc',
    logo: require('../../assets/provider-logos/telia-dk.png'),
    domains: ['telia.dk'] },
  { key: 'telenor', name: 'Telenor', type: 'imap', initials: 'TN',
    bgColor: '#e0f0ff', textColor: '#0066cc',
    logo: require('../../assets/provider-logos/telenor-dk.png'),
    domains: ['telenor.dk', 'email.dk'] },
  { key: 'onecom', name: 'one.com', type: 'imap', initials: '1',
    bgColor: '#fff3e0', textColor: '#e65100',
    logo: require('../../assets/provider-logos/onecom.png'),
    domains: ['one.com'] },
  { key: 'stofa', name: 'Stofa', type: 'imap', initials: 'S',
    bgColor: '#fef3c7', textColor: '#b45309',
    logo: require('../../assets/provider-logos/stofa-dk.png'),
    domains: ['stofanet.dk', 'stofa.dk'] },
  { key: 'waoo', name: 'Waoo', type: 'imap', initials: 'W',
    bgColor: '#dcfce7', textColor: '#16a34a',
    logo: require('../../assets/provider-logos/waoo-dk.png'),
    domains: ['waoo.dk', 'waoomail.dk'] },
  { key: 'jubii', name: 'Jubii', type: 'imap', initials: 'J',
    bgColor: '#fee2e2', textColor: '#dc2626',
    logo: require('../../assets/provider-logos/jubii.png'),
    domains: ['jubii.dk'] },
  { key: 'fullrate', name: 'Fullrate', type: 'imap', initials: 'FR',
    bgColor: '#f0fdfa', textColor: '#0d9488',
    logo: require('../../assets/provider-logos/fullrate.png'),
    domains: ['fullrate.dk'] },
  { key: 'tre', name: '3', type: 'imap', initials: '3',
    bgColor: '#fce7f3', textColor: '#db2777',
    logo: require('../../assets/provider-logos/3-dk.png'),
    domains: ['3.dk'] },
  // International (app password required)
  { key: 'yahoo', name: 'Yahoo', type: 'imap', initials: 'Y',
    bgColor: '#f3e8ff', textColor: '#7c3aed',
    logo: require('../../assets/provider-logos/yahoo.png'),
    domains: ['yahoo.com', 'yahoo.dk'],
    requiresAppPassword: true,
    appPasswordUrl: 'https://login.yahoo.com/account/security' },
  { key: 'icloud', name: 'iCloud', type: 'imap', initials: 'iC',
    bgColor: '#f1f5f9', textColor: '#475569',
    logo: require('../../assets/provider-logos/icloud.png'),
    domains: ['icloud.com', 'me.com', 'mac.com'],
    requiresAppPassword: true,
    appPasswordUrl: 'https://appleid.apple.com/account/manage' },
]

function detectProviderByDomain(email: string): EmailProvider | null {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return null
  return EMAIL_PROVIDERS.find(p => p.domains.includes(domain)) || null
}

// ─── Component ──────────────────────────────────────────────

interface EmailProviderModalProps {
  visible: boolean
  onClose: () => void
  onConnected: () => void
  redirectTo?: 'settings' | 'onboarding'
  consentGiven: boolean
  initialProvider?: string
}

type ModalView = 'grid' | 'form' | 'other' | 'success'

export default function EmailProviderModal({
  visible, onClose, onConnected, redirectTo = 'settings', consentGiven, initialProvider,
}: EmailProviderModalProps) {
  const [view, setView] = useState<ModalView>('grid')
  const [selectedProvider, setSelectedProvider] = useState<EmailProvider | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [otherEmail, setOtherEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const reset = useCallback(() => {
    setView('grid')
    setSelectedProvider(null)
    setEmail('')
    setPassword('')
    setConnecting(false)
    setOtherEmail('')
    setShowPassword(false)
  }, [])

  // Reset state when modal closes (e.g. backdrop tap)
  React.useEffect(() => {
    if (!visible) reset()
  }, [visible, reset])

  // Auto-select provider when opened with initialProvider
  React.useEffect(() => {
    if (visible && initialProvider) {
      const provider = EMAIL_PROVIDERS.find(p => p.key === initialProvider)
      if (provider) {
        setSelectedProvider(provider)
        setView('form')
      }
    }
  }, [visible, initialProvider])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSelectProvider = async (provider: EmailProvider) => {
    if (!consentGiven) {
      toast.error('Du skal acceptere samtykke først')
      return
    }

    if (provider.type === 'oauth') {
      // OAuth — redirect like current flow
      setConnecting(true)
      try {
        const res = await apiClient.email.authorize(provider.key, redirectTo)
        if (res.data?.authorization_url) {
          if (Platform.OS === 'web') {
            window.location.href = res.data.authorization_url
          } else {
            // Native: open auth in browser. After user completes OAuth and
            // dismisses the browser, we poll connection status from the backend.
            const page = redirectTo === 'onboarding' ? 'onboarding' : 'settings'
            const callbackUrl = Linking.createURL(page, { queryParams: { email_connected: 'true' } })
            await WebBrowser.openAuthSessionAsync(
              res.data.authorization_url,
              callbackUrl
            )
            // Sync with Unipile + poll until connection appears
            for (let i = 0; i < 5; i++) {
              try {
                const syncRes = await apiClient.email.sync()
                const connections = syncRes.data?.connections || []
                if (connections.length > 0) {
                  onConnected()
                  setView('success')
                  setConnecting(false)
                  return
                }
              } catch {}
              if (i < 4) await new Promise(r => setTimeout(r, 2000))
            }
          }
          handleClose()
        } else {
          toast.error('Serverfejl: kunne ikke oprette forbindelse')
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        if (detail) toast.error(detail)
        else toast.error('Kunne ikke starte email-forbindelsen')
      } finally {
        setConnecting(false)
      }
      return
    }

    // IMAP — show form
    setSelectedProvider(provider)
    setView('form')
  }

  const handleOtherEmail = () => {
    if (!consentGiven) {
      toast.error('Du skal acceptere samtykke først')
      return
    }

    const detected = detectProviderByDomain(otherEmail)
    if (detected) {
      if (detected.type === 'oauth') {
        handleSelectProvider(detected)
        return
      }
      // IMAP — set provider and pre-fill email, then show form
      setSelectedProvider(detected)
      setEmail(otherEmail)
      setView('form')
    } else {
      // Unknown domain — default to one.com (most custom domains in DK use one.com)
      const onecom = EMAIL_PROVIDERS.find(p => p.key === 'onecom')
      if (!onecom) return
      setSelectedProvider(onecom)
      setEmail(otherEmail)
      setView('form')
      toast.info('Ukendt domæne — prøver one.com indstillinger. Kontakt support hvis det ikke virker.')
    }
  }

  const handleConnect = async () => {
    if (!selectedProvider || !email || !password) return
    setConnecting(true)
    try {
      await apiClient.email.connectImap(email, password, selectedProvider.key)
      onConnected()
      setView('success')
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Kunne ikke forbinde'
      toast.error(detail)
      setPassword('')
    } finally {
      setConnecting(false)
    }
  }

  // ─── Grid View ──────────────────────────────

  const oauthProviders = EMAIL_PROVIDERS.filter(p => p.type === 'oauth')
  const imapProviders = EMAIL_PROVIDERS.filter(p => p.type === 'imap')

  const renderProviderTile = (provider: EmailProvider) => (
    <Pressable
      key={provider.key}
      style={({ pressed }) => [s.providerTile, pressed && { opacity: 0.7, transform: [{ scale: 0.97 }] }]}
      onPress={() => handleSelectProvider(provider)}
      accessibilityLabel={`Forbind med ${provider.name}`}
      accessibilityRole="button"
    >
      <View style={s.providerLogoWrap}>
        {provider.logo ? (
          <Image source={provider.logo} style={s.providerLogo} resizeMode="contain" />
        ) : (
          <Text style={[s.providerInitials, { color: provider.textColor }]}>
            {provider.initials}
          </Text>
        )}
      </View>
      <Text style={s.providerName} numberOfLines={1}>{provider.name}</Text>
    </Pressable>
  )

  const renderGrid = () => (
    <Animated.View entering={FadeIn.duration(200)} style={s.content}>
      <Text style={s.title}>Forbind din email</Text>
      <Text style={s.subtitle}>Vælg din emailudbyder</Text>

      {/* OAuth — recommended, 1-click */}
      <View style={s.sectionRow}>
        {oauthProviders.map((provider) => (
          <Pressable
            key={provider.key}
            style={({ pressed }) => [s.oauthTile, pressed && { opacity: 0.7, transform: [{ scale: 0.98 }] }]}
            onPress={() => handleSelectProvider(provider)}
            accessibilityLabel={`Forbind med ${provider.name}`}
            accessibilityRole="button"
          >
            <View style={s.oauthLogoWrap}>
              {provider.logo && (
                <Image source={provider.logo} style={s.oauthLogo} resizeMode="contain" />
              )}
            </View>
            <View style={s.oauthTextWrap}>
              <Text style={s.oauthName}>{provider.name}</Text>
              <Text style={s.oauthHint}>1-klik forbindelse</Text>
            </View>
          </Pressable>
        ))}
      </View>

      {/* Divider */}
      <View style={s.divider}>
        <View style={s.dividerLine} />
        <Text style={s.dividerText}>eller vælg udbyder</Text>
        <View style={s.dividerLine} />
      </View>

      {/* IMAP providers grid */}
      <View style={s.grid}>
        {imapProviders.map(renderProviderTile)}

        {/* "Anden email" tile */}
        <Pressable
          style={({ pressed }) => [s.providerTile, pressed && { opacity: 0.7, transform: [{ scale: 0.97 }] }]}
          onPress={() => setView('other')}
          accessibilityLabel="Anden emailudbyder"
          accessibilityRole="button"
        >
          <View style={s.providerLogoWrap}>
            <Mail size={20} color={colors.text.muted} />
          </View>
          <Text style={s.providerName}>Anden</Text>
        </Pressable>
      </View>

      <View style={s.trustRow}>
        <Lock size={14} color={colors.text.muted} />
        <Text style={s.trustText}>Krypteret forbindelse — dine data gemmes aldrig hos os</Text>
      </View>
    </Animated.View>
  )

  // ─── IMAP Form View ────────────────────────

  const renderForm = () => {
    if (!selectedProvider) return null
    return (
      <Animated.View entering={FadeInRight.duration(200)} style={s.content}>
        <Pressable style={s.backBtn} onPress={() => { setView('grid'); setEmail(''); setPassword('') }}>
          <ChevronLeft size={20} color={colors.primary} />
          <Text style={s.backText}>Tilbage</Text>
        </Pressable>

        <View style={s.formHeader}>
          <View style={s.formLogoWrap}>
            {selectedProvider.logo ? (
              <Image source={selectedProvider.logo} style={s.formLogo} resizeMode="contain" />
            ) : (
              <Text style={[s.formInitials, { color: selectedProvider.textColor }]}>
                {selectedProvider.initials}
              </Text>
            )}
          </View>
          <Text style={s.formTitle}>Log ind med {selectedProvider.name}</Text>
        </View>

        <View style={s.formFields}>
          <TextInput
            style={s.input}
            value={email}
            onChangeText={setEmail}
            placeholder="din@email.dk"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            placeholderTextColor={colors.text.muted}
            editable={!connecting}
            accessibilityLabel="Email adresse"
            accessibilityHint="Indtast din email adresse"
          />
          <View style={s.passwordContainer}>
            <TextInput
              style={s.passwordInput}
              value={password}
              onChangeText={setPassword}
              placeholder={selectedProvider.requiresAppPassword ? 'App-specifikt kodeord' : 'Adgangskode'}
              secureTextEntry={!showPassword}
              placeholderTextColor={colors.text.muted}
              editable={!connecting}
              accessibilityLabel="Adgangskode"
              accessibilityHint="Indtast din adgangskode"
            />
            <Pressable
              style={s.eyeBtn}
              onPress={() => setShowPassword(!showPassword)}
              accessibilityLabel={showPassword ? 'Skjul adgangskode' : 'Vis adgangskode'}
              accessibilityRole="button"
              hitSlop={8}
            >
              {showPassword
                ? <EyeOff size={20} color={colors.text.muted} />
                : <Eye size={20} color={colors.text.muted} />
              }
            </Pressable>
          </View>
        </View>

        {selectedProvider.requiresAppPassword && (
          <Pressable
            style={s.appPasswordNote}
            onPress={() => selectedProvider.appPasswordUrl && Linking.openURL(selectedProvider.appPasswordUrl)}
          >
            <ExternalLink size={14} color={colors.primary} />
            <Text style={s.appPasswordText}>
              {selectedProvider.name} kræver et app-specifikt kodeord. Tryk her for at oprette det.
            </Text>
          </Pressable>
        )}

        <View style={s.trustBox}>
          <Lock size={14} color={colors.text.muted} />
          <Text style={s.trustBoxText}>
            Dine loginoplysninger sendes via SSL og opbevares krypteret hos vores databehandler (Unipile) i EU.
          </Text>
        </View>

        <Pressable
          style={[s.connectBtn, (!email || !password || connecting) && { opacity: 0.5 }]}
          onPress={handleConnect}
          disabled={!email || !password || connecting}
        >
          {connecting ? (
            <ActivityIndicator size="small" color={colors.text.inverse} />
          ) : (
            <Text style={s.connectBtnText}>Forbind</Text>
          )}
        </Pressable>
      </Animated.View>
    )
  }

  // ─── "Anden email" View ────────────────────

  const renderSuccess = () => (
    <Animated.View entering={FadeIn.duration(200)} style={s.content}>
      <View style={s.successIcon}>
        <CheckCircle2 size={48} color={colors.success} />
      </View>
      <Text style={s.successTitle}>Din email er forbundet</Text>
      <Text style={s.successSubtitle}>Sådan virker det nu</Text>

      <View style={s.successList}>
        <View style={s.successRow}>
          <View style={s.successRowIcon}>
            <Inbox size={18} color={colors.primary} />
          </View>
          <View style={s.successRowText}>
            <Text style={s.successRowTitle}>Vi henter dine provider-emails</Text>
            <Text style={s.successRowBody}>
              Regninger, varsler og kontrakter samles automatisk i appen
            </Text>
          </View>
        </View>

        <View style={s.successRow}>
          <View style={s.successRowIcon}>
            <Send size={18} color={colors.primary} />
          </View>
          <View style={s.successRowText}>
            <Text style={s.successRowTitle}>Vi sender for dig</Text>
            <Text style={s.successRowBody}>
              Tilbudsforespørgsler og korrespondance sendes via ForbrugerAgenten — din egen email berøres aldrig
            </Text>
          </View>
        </View>

        <View style={s.successRow}>
          <View style={s.successRowIcon}>
            <Mail size={18} color={colors.primary} />
          </View>
          <View style={s.successRowText}>
            <Text style={s.successRowTitle}>"ForbrugerAgenten" label i din indbakke</Text>
            <Text style={s.successRowBody}>
              Provider-emails får et label så du nemt kan finde eller arkivere dem i Gmail/Outlook
            </Text>
          </View>
        </View>

        <View style={s.successRow}>
          <View style={s.successRowIcon}>
            <Bell size={18} color={colors.primary} />
          </View>
          <View style={s.successRowText}>
            <Text style={s.successRowTitle}>Du får besked i appen</Text>
            <Text style={s.successRowBody}>
              Push-notifikationer når der er noget vigtigt — resten finder du i indbakken
            </Text>
          </View>
        </View>
      </View>

      <Pressable style={s.connectBtn} onPress={handleClose}>
        <Text style={s.connectBtnText}>Forstået</Text>
      </Pressable>
    </Animated.View>
  )

  const renderOther = () => (
    <Animated.View entering={FadeInRight.duration(200)} style={s.content}>
      <Pressable style={s.backBtn} onPress={() => { setView('grid'); setOtherEmail('') }}>
        <ChevronLeft size={20} color={colors.primary} />
        <Text style={s.backText}>Tilbage</Text>
      </Pressable>

      <Text style={s.formTitle}>Anden emailudbyder</Text>
      <Text style={[s.subtitle, { marginBottom: spacing.md }]}>
        Skriv din email, så finder vi din udbyder
      </Text>

      <TextInput
        style={s.input}
        value={otherEmail}
        onChangeText={setOtherEmail}
        placeholder="din@email.dk"
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        placeholderTextColor={colors.text.muted}
      />

      <Pressable
        style={[s.connectBtn, !otherEmail.includes('@') && { opacity: 0.5 }]}
        onPress={handleOtherEmail}
        disabled={!otherEmail.includes('@')}
      >
        <Text style={s.connectBtnText}>Find udbyder</Text>
      </Pressable>
    </Animated.View>
  )

  // ─── Render ─────────────────────────────────

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        style={s.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <Pressable style={s.backdrop} onPress={handleClose} />
        <View style={s.modal}>
          {/* Close button */}
          <Pressable style={s.closeBtn} onPress={handleClose} accessibilityLabel="Luk">
            <X size={20} color={colors.text.muted} />
          </Pressable>

          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {view === 'grid' && renderGrid()}
            {view === 'form' && renderForm()}
            {view === 'other' && renderOther()}
            {view === 'success' && renderSuccess()}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  )
}

// ─── Styles ───────────────────────────────────────────────

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modal: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    width: '90%',
    maxWidth: 400,
    maxHeight: '85%',
    padding: spacing.lg,
    ...shadows.lg,
  },
  closeBtn: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    zIndex: 10,
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.full,
  },
  content: {
    paddingTop: spacing.sm,
  },

  // Success view
  successIcon: {
    alignItems: 'center',
    marginBottom: spacing.md,
    marginTop: spacing.sm,
  },
  successTitle: {
    ...typography.h2,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  successSubtitle: {
    ...typography.caption,
    color: colors.text.muted,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  successList: {
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  successRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  successRowIcon: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.md,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  successRowText: {
    flex: 1,
  },
  successRowTitle: {
    ...typography.captionBold,
    color: colors.text.primary,
    marginBottom: 2,
  },
  successRowBody: {
    ...typography.small,
    color: colors.text.muted,
    lineHeight: 18,
  },

  // Grid
  title: {
    ...typography.h2,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  subtitle: {
    ...typography.caption,
    color: colors.text.muted,
    marginBottom: spacing.lg,
  },

  // OAuth section — prominent horizontal cards
  sectionRow: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  oauthTile: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.lg,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    gap: spacing.md,
  },
  oauthLogoWrap: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  oauthLogo: {
    width: 36,
    height: 36,
  },
  oauthTextWrap: {
    flex: 1,
  },
  oauthName: {
    ...typography.bodyBold,
    color: colors.text.primary,
  },
  oauthHint: {
    ...typography.small,
    color: colors.text.muted,
    marginTop: 1,
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.borderLight,
  },
  dividerText: {
    ...typography.small,
    color: colors.text.muted,
  },

  // IMAP grid — clean card tiles
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  providerTile: {
    width: '30%',
    alignItems: 'center',
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.xs,
    borderRadius: borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
    backgroundColor: colors.background,
  },
  providerLogoWrap: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  providerInitials: {
    ...typography.bodyBold,
    fontSize: 16,
  },
  providerLogo: {
    width: 36,
    height: 36,
    borderRadius: 8,
  },
  providerName: {
    ...typography.small,
    color: colors.text.secondary,
    textAlign: 'center',
  },

  // Trust
  trustRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  trustText: {
    ...typography.small,
    color: colors.text.muted,
    flex: 1,
  },

  // Form
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: spacing.lg,
    minHeight: 44,
    paddingVertical: spacing.xs,
  },
  backText: {
    ...typography.captionBold,
    color: colors.primary,
  },
  formHeader: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  formLogoWrap: {
    width: 56,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  formInitials: {
    ...typography.h2,
  },
  formLogo: {
    width: 48,
    height: 48,
    borderRadius: 10,
  },
  formTitle: {
    ...typography.h3,
    color: colors.text.primary,
    textAlign: 'center',
  },
  formFields: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  input: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 4,
    ...typography.body,
    color: colors.text.primary,
    minHeight: 48,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  },
  appPasswordNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    backgroundColor: colors.primarySubtle,
    padding: spacing.sm + 4,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  appPasswordText: {
    ...typography.small,
    color: colors.primary,
    flex: 1,
  },
  trustBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  trustBoxText: {
    ...typography.small,
    color: colors.text.muted,
    flex: 1,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  },
  passwordInput: {
    flex: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 4,
    ...typography.body,
    color: colors.text.primary,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  },
  eyeBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  connectBtn: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    paddingVertical: spacing.sm + 4,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  connectBtnText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
  },
})
